import type { VoiceCallOperation, VoiceCallRecord } from '../../src/shared/wireContract';

export interface VoiceCallPresentation { voiceCall: VoiceCallRecord; text: string }

/** Latest observation wins without moving the operation from its first chronological position. */
export function latestVoiceOperations(operations: readonly VoiceCallOperation[]): VoiceCallOperation[] {
  return [...new Map(operations.map(operation => [operation.id, operation])).values()];
}

interface VoiceTurn { role: string; id?: string; text?: string; createdAt?: string; voiceCall?: VoiceCallRecord }

/** Presentation only: null suppresses a receipt, an object replaces the first call row.
 * Indices and native turns stay untouched, including live conversation-append suffixes.
 * Each started interpretation can admit one following unkeyed transcript segment.
 * Admission, its outcome or a tail closes that association; late outcomes use their call id. */
export function projectVoiceCallTurns(turns: readonly VoiceTurn[]): ReadonlyMap<number, VoiceCallPresentation | null> {
  const rows = turns.flatMap((turn, index) => turn.role === 'you' && turn.voiceCall
    ? [{ index, turn, call: turn.voiceCall }] : []);
  rows.sort((a, b) => a.turn.createdAt && b.turn.createdAt
    ? Date.parse(a.turn.createdAt) - Date.parse(b.turn.createdAt) || a.index - b.index : a.index - b.index);
  const groups = new Map<string | number, { indices: number[]; text: string[]; calls: VoiceCallRecord[] }>();
  let pending: { callId: string; operationId: string } | undefined;
  for (const { index, turn, call } of rows) {
    const [prefix, callId, kind] = (turn.id ?? '').split(':');
    const keyed = prefix === 'voice-call' && callId && (kind === 'operation' || kind === 'tail') ? callId : undefined;
    const started = call.operations?.find(operation => operation.status === 'started');
    if (keyed && started) pending = { callId: keyed, operationId: started.id };
    const key = keyed ?? pending?.callId ?? index;
    let group = groups.get(key);
    if (!group) { group = { indices: [], text: [], calls: [] }; groups.set(key, group); }
    group.indices.push(index);
    group.calls.push(call);
    if (turn.text?.trim()) group.text.push(turn.text);
    if (!keyed || (keyed === pending?.callId && (kind === 'tail'
      || call.operations?.some(operation => operation.id === pending?.operationId && operation.status !== 'started')))) pending = undefined;
  }
  const projection = new Map<number, VoiceCallPresentation | null>();
  for (const group of groups.values()) {
    const transcript = group.calls.flatMap(call => call.transcript);
    const operations = latestVoiceOperations(group.calls.flatMap(call => call.operations ?? []));
    const transcriptCalls = group.calls.filter(call => call.transcript.length);
    const voiceCall: VoiceCallRecord = {
      durationMs: Math.max(...(transcriptCalls.length ? transcriptCalls : group.calls).map(call => call.durationMs)),
      transcript,
      ...(operations.length ? { operations } : {}),
    };
    for (const index of group.indices) projection.set(index, null);
    projection.set(Math.min(...group.indices), { voiceCall, text: group.text.join('\n') });
  }
  return projection;
}
